/**
 * JoinPage — landing page for invite links.
 *
 * URL: /join/:code
 *
 * Someone receives an invite link from a farm admin, clicks it, and lands
 * here. They can sign in / sign up with Google, Apple, or email.
 * The invite code is carried through the OAuth state so the backend can
 * automatically associate the new user with the correct tenant.
 *
 * If the invite code is invalid or expired, we show an error instead.
 */
import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { tenantApi } from '../lib/api'

const BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:8080'

const C = {
  bg:     'var(--color-bg)',
  card:   'var(--color-bg-card)',
  border: 'rgba(255,255,255,0.08)',
  amber:  '#F5A623',
  text:   'var(--color-text)',
  muted:  'var(--color-text-muted)',
  faint:  'var(--color-text-faint)',
  danger: '#F87171',
}

export default function JoinPage() {
  const { code } = useParams<{ code: string }>()
  const navigate = useNavigate()
  const [farm, setFarm]     = useState<{ farm_name: string; logo_url?: string; slug: string } | null>(null)
  const [error, setError]   = useState('')
  const [loading, setLoading] = useState(true)
  const [emailMode, setEmailMode] = useState(false)
  const [email, setEmail]   = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitErr, setSubmitErr] = useState('')

  // Which social-login providers the backend has credentials for. Apple is hidden
  // until APPLE_CLIENT_ID is configured (§200); it reappears automatically then.
  const [oauthProviders, setOauthProviders] = useState<string[]>(['google'])
  useEffect(() => {
    let alive = true
    fetch(`${BASE}/api/v1/auth/providers`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (alive && d && Array.isArray(d.providers)) setOauthProviders(d.providers) })
      .catch(() => {})
    return () => { alive = false }
  }, [])

  useEffect(() => {
    if (!code) { setError('Invalid invite link.'); setLoading(false); return }
    tenantApi.resolveInvite(code)
      .then(r => { setFarm(r.data); setLoading(false) })
      .catch(() => { setError('This invite link is invalid or has expired.'); setLoading(false) })
  }, [code])

  function startOAuth(provider: string) {
    const params = new URLSearchParams({
      redirect_uri: window.location.origin + '/login',
      invite_code: code ?? '',
    })
    window.location.href = `${BASE}/api/v1/auth/oauth/${provider}/begin?${params}`
  }

  async function handleEmailJoin(e: React.FormEvent) {
    e.preventDefault()
    if (!email || !password) { setSubmitErr('Email and password are required.'); return }
    setSubmitting(true)
    setSubmitErr('')
    try {
      const res = await fetch(`${BASE}/api/v1/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, invite_code: code }),
      })
      if (res.ok) {
        navigate(`/login?invite=${code}`, { replace: true })
      } else {
        const d = await res.json()
        setSubmitErr(d.error || 'Something went wrong. Try again.')
      }
    } catch {
      setSubmitErr('Network error. Check your connection.')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <main style={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.bg }}>
        <p style={{ color: C.faint, fontSize: 14 }}>Loading invite…</p>
      </main>
    )
  }

  if (error) {
    return (
      <main style={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.bg, padding: 24 }}>
        <div style={{ textAlign: 'center', maxWidth: 360 }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>🔗</div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: C.text, margin: '0 0 8px' }}>Invite Not Found</h1>
          <p style={{ fontSize: 14, color: C.muted, margin: '0 0 24px' }}>{error}</p>
          <p style={{ fontSize: 13, color: C.faint }}>
            Ask your farm admin for a fresh invite link, or{' '}
            <Link to="/login" style={{ color: C.amber }}>sign in</Link> if you already have an account.
          </p>
        </div>
      </main>
    )
  }

  return (
    <main style={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.bg, padding: 24 }}>
      <div style={{ width: '100%', maxWidth: 400 }}>

        {/* Header */}
        <header style={{ textAlign: 'center', marginBottom: 32 }}>
          {farm?.logo_url ? (
            <img src={farm.logo_url} alt={farm.farm_name} style={{ height: 56, objectFit: 'contain', borderRadius: 10, marginBottom: 16 }} />
          ) : (
            <div style={{ fontSize: 40, marginBottom: 12 }}>🌿</div>
          )}
          <h1 style={{ fontSize: 22, fontWeight: 700, color: C.text, margin: '0 0 6px', fontFamily: '"Syne", system-ui, sans-serif' }}>
            {farm?.farm_name}
          </h1>
          <p style={{ fontSize: 14, color: C.muted, margin: 0 }}>
            You've been invited to join this farm on Grafty.
          </p>
        </header>

        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 28 }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, color: C.text, margin: '0 0 20px', textAlign: 'center' }}>
            Accept your invitation
          </h2>

          {!emailMode ? (
            <>
              {/* Primary OAuth options */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
                <button
                  onClick={() => startOAuth('google')}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'center',
                    height: 48, background: '#fff', border: 'none', borderRadius: 10,
                    fontSize: 15, fontWeight: 500, cursor: 'pointer', color: '#1f1f1f', width: '100%',
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </button>

                {/* Apple — hidden until APPLE_CLIENT_ID is configured (§200) */}
                {oauthProviders.includes('apple') && (
                <button
                  onClick={() => startOAuth('apple')}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'center',
                    height: 48, background: '#000', border: 'none', borderRadius: 10,
                    fontSize: 15, fontWeight: 500, cursor: 'pointer', color: '#fff', width: '100%',
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="white" aria-hidden="true">
                    <path d="M17.05 12.04c-.03-2.79 2.28-4.13 2.39-4.2-1.3-1.9-3.32-2.16-4.04-2.19-1.72-.17-3.36 1.01-4.23 1.01-.87 0-2.22-.99-3.65-.96-1.88.03-3.61 1.09-4.58 2.77-1.95 3.38-.5 8.39 1.4 11.14.93 1.35 2.04 2.86 3.5 2.81 1.4-.06 1.93-.9 3.63-.9 1.69 0 2.17.9 3.65.87 1.51-.03 2.46-1.37 3.38-2.73 1.07-1.57 1.51-3.09 1.53-3.17-.03-.01-2.94-1.13-2.97-4.47zM14.28 3.84c.77-.93 1.29-2.22 1.15-3.51-1.11.05-2.46.74-3.25 1.67-.71.82-1.33 2.14-1.16 3.4 1.24.1 2.5-.63 3.26-1.56z"/>
                  </svg>
                  Continue with Apple
                </button>
                )}
              </div>

              {/* Divider */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <div style={{ flex: 1, height: 1, background: C.border }} />
                <span style={{ fontSize: 12, color: C.faint }}>or</span>
                <div style={{ flex: 1, height: 1, background: C.border }} />
              </div>

              <button
                onClick={() => setEmailMode(true)}
                style={{
                  width: '100%', height: 44, background: 'transparent', border: `1px solid ${C.border}`,
                  borderRadius: 10, fontSize: 14, color: C.muted, cursor: 'pointer',
                }}
              >
                Continue with email & password
              </button>
            </>
          ) : (
            /* Email form */
            <form onSubmit={handleEmailJoin} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ fontSize: 11, color: C.muted, display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Email Address
                </label>
                <input
                  type="email" value={email} onChange={e => setEmail(e.target.value)} required
                  style={{ width: '100%', boxSizing: 'border-box', background: 'var(--color-bg-hover)', border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 12px', color: C.text, fontSize: 14, outline: 'none' }}
                  placeholder="you@example.com"
                />
              </div>
              <div>
                <label style={{ fontSize: 11, color: C.muted, display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Create Password
                </label>
                <input
                  type="password" value={password} onChange={e => setPassword(e.target.value)} required minLength={8}
                  style={{ width: '100%', boxSizing: 'border-box', background: 'var(--color-bg-hover)', border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 12px', color: C.text, fontSize: 14, outline: 'none' }}
                  placeholder="At least 8 characters"
                />
              </div>
              {submitErr && (
                <p style={{ fontSize: 12, color: C.danger, margin: 0 }}>{submitErr}</p>
              )}
              <button
                type="submit" disabled={submitting}
                style={{ height: 46, background: C.amber, border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 600, color: 'var(--color-bg)', cursor: 'pointer', marginTop: 4 }}
              >
                {submitting ? 'Creating account…' : 'Join Farm'}
              </button>
              <button
                type="button" onClick={() => setEmailMode(false)}
                style={{ background: 'none', border: 'none', color: C.faint, fontSize: 13, cursor: 'pointer' }}
              >
                ← Back to sign-in options
              </button>
            </form>
          )}

          {/* Already have account */}
          <p style={{ textAlign: 'center', fontSize: 13, color: C.faint, marginTop: 20, marginBottom: 0 }}>
            Already have a Grafty account?{' '}
            <Link to={`/login?invite=${code}`} style={{ color: C.amber, textDecoration: 'none' }}>
              Sign in instead
            </Link>
          </p>
        </div>

        <p style={{ fontSize: 11, color: C.faint, textAlign: 'center', marginTop: 20, lineHeight: 1.6 }}>
          By joining, you agree to Grafty's{' '}
          <a href="https://grafty.io/terms" style={{ color: C.muted }}>Terms of Service</a>
          {' '}and{' '}
          <a href="https://grafty.io/privacy" style={{ color: C.muted }}>Privacy Policy</a>.
        </p>
      </div>
    </main>
  )
}
